import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import type { HelpArticle, NavNode } from "./lib/types";
import { fetchAllPublishedArticles } from "./lib/data";
import { buildNavTree } from "./lib/navigation";
import { Layout } from "./components/Layout";
import { AuthProvider } from "./components/AuthContext";
import { AdminGuard } from "./components/AdminGuard";
import { HelpPage } from "./pages/HelpPage";
import { LoginPage } from "./pages/LoginPage";
import { AdminListPage } from "./pages/AdminListPage";
import { AdminEditPage } from "./pages/AdminEditPage";
import { AdminRevisionsPage } from "./pages/AdminRevisionsPage";

export default function App() {
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [navTree, setNavTree] = useState<NavNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await fetchAllPublishedArticles();
        if (cancelled) return;
        setArticles(data);
        setNavTree(buildNavTree(data));
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Unknown error");
        setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Loading Help Centre...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h1>Help Centre Unavailable</h1>
        <p>The Help Centre could not connect to its database. Please check back later.</p>
      </div>
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/help/login" element={<LoginPage />} />
          <Route
            path="/admin/help"
            element={
              <AdminGuard>
                <AdminListPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/help/new"
            element={
              <AdminGuard>
                <AdminEditPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/help/edit/:id"
            element={
              <AdminGuard>
                <AdminEditPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/help/revisions/:id"
            element={
              <AdminGuard>
                <AdminRevisionsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/*"
            element={
              <Layout navTree={navTree} articles={articles}>
                <Routes>
                  <Route path="/*" element={<HelpPage allArticles={articles} navTree={navTree} />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
